import { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@vd/ui';
import styles from './Admin.module.css';

/**
 * Reusable admin table — sort, search, optional per-page filter row.
 *
 * Five admin tables (users, accounts, usage, cancellations,
 * overrides) need the same affordances; writing each one by hand
 * would mean five copies of the same sort + search code. Keep this
 * component pure UI + state — backend pages still own their queries
 * and the cell renderers.
 *
 * Sorting + searching are client-side. Acceptable up to ~10k rows
 * for the data we have today; switch to server-side sort/search when
 * a real account starts pushing that limit.
 *
 * Filter affordance is hoisted into a render prop (`filterBar`) so
 * each page can hand-roll the controls — plan dropdown on
 * Accounts/Usage, source toggle on Cancellations, etc. The table
 * doesn't try to be opinionated about WHAT to filter, just stays
 * out of the way.
 */

export interface AdminTableColumn<T> {
  /** Stable id used for sort state + DOM keys. Must be unique. */
  key: string;
  /** Header label. */
  label: string;
  /** True → header is clickable, column is sortable on this key. */
  sortable?: boolean;
  /** Numeric/right-aligned styling hint. */
  numeric?: boolean;
  /** Center-aligned styling hint. */
  center?: boolean;
  /** Optional tooltip on the header cell. */
  title?: string;
  /**
   * Pull the sort key off a row. When omitted, the column isn't
   * sortable regardless of `sortable`. Should return a string,
   * number, or null/undefined (which sorts last).
   */
  sortAccessor?: (row: T) => string | number | null | undefined;
  /** Render the cell content. */
  render: (row: T) => React.ReactNode;
}

interface AdminTableProps<T> {
  columns: AdminTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Function returning the searchable text for a row. Multi-field
   *  concatenation is the caller's responsibility (name + email +
   *  id, etc.). Case-insensitive matching is applied internally. */
  searchTextFor?: (row: T) => string;
  searchPlaceholder?: string;
  /** Optional initial sort key. */
  initialSortKey?: string;
  initialSortDir?: 'asc' | 'desc';
  /** Optional render-prop for per-page filter controls. Receives
   *  the current row count after search so you can show "N of M". */
  filterBar?: (visibleRowCount: number, totalRowCount: number) => React.ReactNode;
  /** Empty-state message when no rows match the search/filter. */
  emptyText?: string;
  /** Optional className for the wrapping section. */
  className?: string;
  /** Optional whole-row click handler. */
  onRowClick?: (row: T) => void;
  /** Optional row id whose surrounding row gets a "highlighted"
   *  treatment. Driven by ?focus= query string on the page. */
  highlightRowId?: string | null;
}

export function AdminTable<T>(props: AdminTableProps<T>) {
  const { columns, rows, getRowId, searchTextFor, searchPlaceholder = 'Search…',
    initialSortKey, initialSortDir = 'desc', filterBar, emptyText = 'No rows.',
    onRowClick, highlightRowId } = props;

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(initialSortKey ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (q && searchTextFor) {
      out = out.filter((r) => searchTextFor(r).toLowerCase().includes(q));
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      const acc = col?.sortAccessor;
      if (acc) {
        const dir = sortDir === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => compareValues(acc(a), acc(b)) * dir);
      }
    }
    return out;
  }, [rows, search, sortKey, sortDir, columns, searchTextFor]);

  // Scroll the highlighted row into view when ?focus= changes.
  const tbodyRef = useRef<HTMLTableSectionElement | null>(null);
  useEffect(() => {
    if (!highlightRowId || !tbodyRef.current) return;
    const el = tbodyRef.current.querySelector<HTMLTableRowElement>(`[data-row-id="${cssEscape(highlightRowId)}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightRowId, visibleRows]);

  function toggleSort(key: string) {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('desc');
      return;
    }
    if (sortDir === 'desc') setSortDir('asc');
    else { setSortKey(null); setSortDir('desc'); }
  }

  return (
    <div className={cn(styles.section, props.className)}>
      <div className={styles.tableControls}>
        {searchTextFor && (
          <div className={styles.searchBox}>
            <Search size={12} className={styles.muted} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={styles.searchInput}
            />
          </div>
        )}
        {filterBar ? filterBar(visibleRows.length, rows.length) : null}
        <span className={cn(styles.muted, styles.tableCount)}>
          {search.trim() && rows.length !== visibleRows.length
            ? `${visibleRows.length} of ${rows.length}`
            : `${rows.length}`}
        </span>
      </div>

      {visibleRows.length === 0 ? (
        <p className={styles.muted}>{emptyText}</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(c.center && styles.center, c.sortable && styles.sortable)}
                  title={c.title}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                  style={c.numeric ? { textAlign: 'right' } : undefined}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {c.label}
                    {c.sortable && (
                      sortKey !== c.key ? <ChevronsUpDown size={11} className={styles.muted} />
                      : sortDir === 'asc' ? <ChevronUp size={11} />
                      : <ChevronDown size={11} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {visibleRows.map((row) => {
              const id = getRowId(row);
              const highlighted = highlightRowId === id;
              return (
                <tr
                  key={id}
                  data-row-id={id}
                  className={cn(highlighted && styles.highlighted, onRowClick && styles.clickableRow)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(c.center && styles.center)}
                      style={c.numeric ? { textAlign: 'right' } : undefined}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Three-way compare that pushes null / undefined to the end. */
function compareValues(a: string | number | null | undefined, b: string | number | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls last
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Minimal CSS.escape polyfill (avoids depending on lib.dom-only) */
function cssEscape(value: string): string {
  if (typeof window !== 'undefined' && (window.CSS && window.CSS.escape)) return window.CSS.escape(value);
  return value.replace(/[^A-Za-z0-9_-]/g, (ch) => `\\${ch}`);
}

/**
 * Convenience hook for reading `?focus=<id>` from the URL. Used by
 * any page that wants to scroll-into-view + highlight a target row
 * after the user clicked a cross-page link.
 */
export function useFocusParam(): string | null {
  const [params] = useSearchParams();
  return params.get('focus');
}
